import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "./axios";
import {
  notificationBaseURL,
  testBaseURL,
  testBaseURLV2,
  trainingBaseURL,
  trainingBaseURLV2,
  userBaseURL,
  userBaseURLV2,
} from "./config";

type QueryParams = Record<string, string | number | boolean | null | undefined>;
type RequestConfig = AxiosRequestConfig;
type Payload = unknown;

const JSON_HEADERS = { "Content-Type": "application/json" };
const FORM_HEADERS = { "Content-Type": "multipart/form-data" };

const withHeaders = (headers: Record<string, string>, requestConfig: RequestConfig): RequestConfig => ({
  ...requestConfig,
  headers: {
    ...headers,
    ...(requestConfig.headers ?? {}),
  },
});

const buildUrl = (baseURL: string, path: string, query?: QueryParams): string => {
  const normalizedBase = baseURL.replace(/\/+$/, "");
  const normalizedPath = String(path ?? "").replace(/^\/+/, "");
  const queryString = query ? new URLSearchParams(query as Record<string, string>).toString() : "";
  const fullPath = `${normalizedBase}/${normalizedPath}`;
  return queryString ? `${fullPath}?${queryString}` : fullPath;
};

const createApiClient = (baseURL: string) => ({
  get: (path: string, query: QueryParams = {}, requestConfig: RequestConfig = {}) =>
    axios.get(buildUrl(baseURL, path, query), requestConfig),
  postJson: (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
    axios.post(buildUrl(baseURL, path), data, withHeaders(JSON_HEADERS, requestConfig)),
  postForm: (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
    axios.post(buildUrl(baseURL, path), data, withHeaders(FORM_HEADERS, requestConfig)),
  put: (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
    axios.put(buildUrl(baseURL, path), data, requestConfig),
  patch: (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
    axios.patch(buildUrl(baseURL, path), data, requestConfig),
  delete: (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
    axios.delete(buildUrl(baseURL, path), { ...requestConfig, data }),
});

const userApi = createApiClient(userBaseURL);
const userApiV2 = createApiClient(userBaseURLV2 || userBaseURL);
const trainingApi = createApiClient(trainingBaseURL);
const trainingApiV2 = createApiClient(trainingBaseURLV2 || trainingBaseURL);
const testApi = createApiClient(testBaseURL);
const testApiV2 = createApiClient(testBaseURLV2 || testBaseURL);
const notificationApi = createApiClient(notificationBaseURL);

export const getData = (path: string, query: QueryParams = {}, requestConfig: RequestConfig = {}) =>
  userApi.get(path, query, requestConfig);
export const getDataUserV2 = (path: string, query: QueryParams = {}, requestConfig: RequestConfig = {}) =>
  userApiV2.get(path, query, requestConfig);
export const jsonPostData = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApi.postJson(path, data, requestConfig);
export const jsonPostDataUserV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApiV2.postJson(path, data, requestConfig);
export const formPostData = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApi.postForm(path, data, requestConfig);
export const formPostDataUserV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApiV2.postForm(path, data, requestConfig);
export const putData = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApi.put(path, data, requestConfig);
export const putDataUserV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApiV2.put(path, data, requestConfig);
export const patchData = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApi.patch(path, data, requestConfig);
export const patchDataUserV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  userApiV2.patch(path, data, requestConfig);
export const deleteItem = (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
  userApi.delete(path, data, requestConfig);
export const deleteItemUserV2 = (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
  userApiV2.delete(path, data, requestConfig);

export const getDataTraining = (path: string, requestConfig: RequestConfig = {}) =>
  trainingApi.get(path, {}, requestConfig);
export const getDataTrainingV2 = (path: string, requestConfig: RequestConfig = {}) =>
  trainingApiV2.get(path, {}, requestConfig);
export const jsonPostDataTraining = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApi.postJson(path, data, requestConfig);
export const jsonPostDataTrainingV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApiV2.postJson(path, data, requestConfig);
export const formPostDataTraining = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApi.postForm(path, data, requestConfig);
export const formPostDataTrainingV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApiV2.postForm(path, data, requestConfig);
export const putDataTraining = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApi.put(path, data, requestConfig);
export const putDataTrainingV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApiV2.put(path, data, requestConfig);
export const patchDataTraining = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApi.patch(path, data, requestConfig);
export const patchDataTrainingV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  trainingApiV2.patch(path, data, requestConfig);
export const deleteItemTraining = (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
  trainingApi.delete(path, data, requestConfig);
export const deleteItemTrainingV2 = (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
  trainingApiV2.delete(path, data, requestConfig);

export const getDataTest = (path: string, requestConfig: RequestConfig = {}) =>
  testApi.get(path, {}, requestConfig);
export const getDataTestV2 = (path: string, requestConfig: RequestConfig = {}) =>
  testApiV2.get(path, {}, requestConfig);
export const jsonPostDataTest = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApi.postJson(path, data, requestConfig);
export const jsonPostDataTestV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApiV2.postJson(path, data, requestConfig);
export const formPostDataTest = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApi.postForm(path, data, requestConfig);
export const formPostDataTestV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApiV2.postForm(path, data, requestConfig);
export const putDataTest = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApi.put(path, data, requestConfig);
export const putDataTestV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApiV2.put(path, data, requestConfig);
export const patchDataTest = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApi.patch(path, data, requestConfig);
export const patchDataTestV2 = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  testApiV2.patch(path, data, requestConfig);
export const deleteItemTest = (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
  testApi.delete(path, data, requestConfig);
export const deleteItemTestV2 = (path: string, data: Payload = {}, requestConfig: RequestConfig = {}) =>
  testApiV2.delete(path, data, requestConfig);

export const getDataNotification = (path: string, requestConfig: RequestConfig = {}) =>
  notificationApi.get(path, {}, requestConfig);
export const jsonPostDataNotification = (
  path: string,
  data: Payload,
  requestConfig: RequestConfig = {},
) => notificationApi.postJson(path, data, requestConfig);
export const formPostDataNotification = (
  path: string,
  data: Payload,
  requestConfig: RequestConfig = {},
) => notificationApi.postForm(path, data, requestConfig);
export const putDataNotification = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  notificationApi.put(path, data, requestConfig);
export const patchDataNotification = (path: string, data: Payload, requestConfig: RequestConfig = {}) =>
  notificationApi.patch(path, data, requestConfig);
export const deleteItemNotification = (
  path: string,
  data: Payload = {},
  requestConfig: RequestConfig = {},
) => notificationApi.delete(path, data, requestConfig);

export const fetchComments = async (
  spotlightId: string | number,
  pageNumber: number,
  pageSize: number,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<unknown[]> => {
  const response = await getDataUserV2(
    `admin/feeds/${spotlightId}/comments`,
    {
      page: pageNumber,
      page_size: pageSize,
      feed_type: feedType,
    },
    requestConfig,
  );
  return response.data || [];
};

export const postComment = async (
  spotlightId: string | number,
  text: string,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<unknown> => {
  const response = await jsonPostDataUserV2(
    `admin/feeds/${spotlightId}/comments`,
    {
      comment: text,
      feed_type: feedType,
    },
    requestConfig,
  );
  return response.data;
};

export const updateComment = async (
  spotlightId: string | number,
  id: string | number,
  text: string,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<unknown> => {
  const response = await putDataUserV2(
    `admin/feeds/${spotlightId}/comments/${id}`,
    {
      comment: text,
      feed_type: feedType,
    },
    requestConfig,
  );
  return response.data;
};

export const deleteComment = async (
  spotlightId: string | number,
  id: string | number,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<boolean> => {
  await deleteItemUserV2(
    `admin/feeds/${spotlightId}/comments/${id}`,
    {
      feed_type: feedType,
    },
    requestConfig,
  );
  return true;
};

export const fetchTwilioAccess = async (
  spotlightId: string | number,
  feedType: string,
  requestConfig: RequestConfig = {},
): Promise<{ token: string; channel_sid: string }> => {
  const response = await getDataUserV2(
    `admin/feeds/access/token/${spotlightId}`,
    {
      feed_type: feedType,
    },
    requestConfig,
  );

  return {
    token: response.data.access_token,
    channel_sid: response.data.chatroom.twilio_channel_sid,
  };
};
